/**
 * Hook for accessing the ITaskStore abstraction layer.
 *
 * This hook provides access to the Zustand task store implementation.
 */

import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { createTaskStore, type ITaskStore } from '../stores';

// Context for the task store
const TaskStoreContext = createContext<ITaskStore | null>(null);

/**
 * Hook to access the task store
 *
 * @returns ITaskStore instance
 * @throws Error if used outside of TaskStoreProvider
 */
export function useTaskStore(): ITaskStore {
  const store = useContext(TaskStoreContext);

  if (!store) {
    throw new Error(
      'useTaskStore must be used within a TaskStoreProvider. ' +
      'Make sure to wrap your component tree with <TaskStoreProvider projectId="...">'
    );
  }

  return store;
}

/**
 * Props for TaskStoreProvider
 */
interface TaskStoreProviderProps {
  children: ReactNode;
  projectId: string;
  /** Enable performance monitoring (default: true) */
  enablePerfMonitoring?: boolean;
}

/**
 * Provider component for the task store
 *
 * Creates and provides the appropriate store implementation based on
 * environment configuration or explicit override.
 */
export function TaskStoreProvider({
  children,
  projectId,
  enablePerfMonitoring = true,
}: TaskStoreProviderProps) {
  const [store, setStore] = useState<ITaskStore | null>(null);

  useEffect(() => {
    console.log(`[TaskStoreProvider] Initializing zustand store for project: ${projectId}`);

    const newStore = createTaskStore(projectId, {
      enablePerfMonitoring,
    });

    setStore(newStore);

    // Cleanup on unmount (if needed in future)
    return () => {
      console.log('[TaskStoreProvider] Cleaning up zustand store');
    };
  }, [projectId, enablePerfMonitoring]);

  if (!store) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>Initializing Zustand store...</p>
      </div>
    );
  }

  return (
    <TaskStoreContext.Provider value={store}>
      {children}
    </TaskStoreContext.Provider>
  );
}

/**
 * Hook to get the current store type
 */
export function useStoreType(): 'zustand' {
  return 'zustand';
}

/**
 * Hook for reactive task list (with subscription)
 * Re-renders when tasks change
 */
export function useTasks() {
  const store = useTaskStore();
  const [tasks, setTasks] = useState(store.getTasks());

  useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      setTasks(store.getTasks());
    });

    return unsubscribe;
  }, [store]);

  return tasks;
}

/**
 * Hook for reactive dhtmlx tasks (with subscription)
 * Re-renders when tasks change
 *
 * PERFORMANCE: Uses Zustand's built-in selector + shallow comparison
 * to avoid recomputing getDhtmlxTasks on every store change
 */
export function useDhtmlxTasks() {
  const store = useTaskStore();

  const [tasks, setTasks] = useState(() => store.getDhtmlxTasks());
  const revisionsRef = useRef({
    tasks: store.getTasksRevision(),
    links: store.getLinksRevision(),
  });

  useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      const tasksRev = store.getTasksRevision();
      const linksRev = store.getLinksRevision();
      const prev = revisionsRef.current;
      if (tasksRev === prev.tasks && linksRev === prev.links) {
        return;
      }
      revisionsRef.current = { tasks: tasksRev, links: linksRev };
      const next = store.getDhtmlxTasks();
      // Field-only patches mutate the cached array in place — keep the same reference so
      // ReactGantt does not deep-diff ~15k rows. Links-only changes also keep identity;
      // Predecessor/Successor refresh via targeted refreshTask / link-pointer rebuild.
      setTasks((prev) => (prev === next ? prev : next));
    });

    return unsubscribe;
  }, [store]);

  return tasks;
}

/**
 * Subscribe to links revision without forcing a new dhtmlxTasks array identity.
 * Keeps Predecessor/Successor indexes fresh after link-only edits.
 */
export function useLinksRevision(): number {
  const store = useTaskStore();
  const [revision, setRevision] = useState(() => store.getLinksRevision());

  useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      const next = store.getLinksRevision();
      setRevision((prev) => (prev === next ? prev : next));
    });
    return unsubscribe;
  }, [store]);

  return revision;
}

/** Subscribe only to selected task id changes (avoids parent re-renders on unrelated store updates). */
export function useSelectedTaskId(): string | null {
  const store = useTaskStore();
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(
    () => store.getSelectedTaskId()
  );

  useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      const next = store.getSelectedTaskId();
      setSelectedTaskId((prev) => (prev === next ? prev : next));
    });
    return unsubscribe;
  }, [store]);

  return selectedTaskId;
}

/**
 * Hook for reactive location tree from the task store.
 * Re-renders when location tree is set (e.g. after fetch or hydrate).
 */
export function useLocationTree() {
  const store = useTaskStore();
  const [tree, setTree] = useState(() => store.getLocationTree?.() ?? null);

  useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      setTree(store.getLocationTree?.() ?? null);
    });
    return unsubscribe;
  }, [store]);

  return tree;
}

/**
 * Hook for reactive loading state
 */
export function useTaskStoreLoading() {
  const store = useTaskStore();
  const [loading, setLoading] = useState(store.isLoading());

  useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      setLoading(store.isLoading());
    });

    return unsubscribe;
  }, [store]);

  return loading;
}

/**
 * Subscribe to session dirty counts without depending on the 15k-row Gantt task array.
 * Field-only store patches keep `useDhtmlxTasks` stable so this is what updates Save.
 */
export function useSessionMetadata() {
  const store = useTaskStore();
  const [meta, setMeta] = useState(() => store.getSessionMetadata());

  useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      const next = store.getSessionMetadata();
      setMeta((prev) => {
        if (
          prev.taskCount === next.taskCount &&
          prev.linkCount === next.linkCount &&
          prev.lastChange === next.lastChange
        ) {
          return prev;
        }
        return next;
      });
    });
    return unsubscribe;
  }, [store]);

  return meta;
}

function sameStringSet(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const id of a) {
    if (!b.has(id)) return false;
  }
  return true;
}

function sameFieldsMap(
  a: Map<string, Set<string>>,
  b: Map<string, Set<string>>,
): boolean {
  if (a.size !== b.size) return false;
  for (const [id, fields] of a) {
    const other = b.get(id);
    if (!other || !sameStringSet(fields, other)) return false;
  }
  return true;
}

/**
 * Task IDs with unsaved changes (pending yellow rows).
 * Includes directly edited tasks and cascade/auto-schedule dependents in the save delta.
 */
export function usePendingChangeTaskIds(): Set<string> {
  const store = useTaskStore();
  const build = () => new Set(store.getAffectedTaskIds());
  const [ids, setIds] = useState(build);

  useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      const next = build();
      setIds((prev) => (sameStringSet(prev, next) ? prev : next));
    });
    return unsubscribe;
  }, [store]);

  return ids;
}

/**
 * Per-task edited column names for bolding changed cell values.
 */
export function usePendingEditedFields(): Map<string, Set<string>> {
  const store = useTaskStore();
  const build = () => {
    const next = new Map<string, Set<string>>();
    const raw = store.getEditedTaskFieldsMap();
    for (const [id, fields] of Object.entries(raw)) {
      next.set(id, new Set(fields));
    }
    return next;
  };
  const [map, setMap] = useState(build);

  useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      const next = build();
      setMap((prev) => (sameFieldsMap(prev, next) ? prev : next));
    });
    return unsubscribe;
  }, [store]);

  return map;
}
